import React from 'react';
import { Button, Typography, Tooltip, Dropdown, Menu, Tag } from 'antd';
import styled from 'styled-components';
import {
    getExecutionRequestStatusDisplayColor,
    getExecutionRequestStatusDisplayText,
    CLI_INGESTION_SOURCE,
    SCHEDULED_INGESTION_SOURCE,
    MANUAL_INGESTION_SOURCE,
    RUNNING,
    SUCCESS,
} from '../utils';
import { ReactComponent as IconMoreVert } from '../../../../images/icon-more-vert.svg';
import i18n from '../../../../i18n/i18n';

const Wrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export function TimeColumn(time: string) {
    const { t } = i18n;
    const date = time && new Date(time);
    const localTime = date && `${date.toLocaleDateString()} ${t('at')} ${date.toLocaleTimeString()}`;
    return <Typography.Text>{localTime || t('None')}</Typography.Text>;
}

interface StatusColumnProps {
    record: any;
    status: string;
    t: (key: string) => string;
    setFocusExecutionUrn: (urn: string) => void;
}

export function StatusColumn({ t, status, record, setFocusExecutionUrn }: StatusColumnProps) {
    const text = t(getExecutionRequestStatusDisplayText(status));
    const color = getExecutionRequestStatusDisplayColor(status);

    let statusClass = 'f-tag-gray-outline';
    if (color === 'green') {
        statusClass = 'ant-tag-success';
    } else if (color === 'red') {
        statusClass = 'des-tag';
    }

    return (
        <Tag
            onClick={() => setFocusExecutionUrn(record.urn)}
            className={`label-tag f-outlined ma-0 ${statusClass} ${text === 'Succeeded' ? 'clickable' : ''}`}
        >
            {text || t('Pending')}
        </Tag>
    );
}

export function SourceColumn(source: string) {
    return (
        <Tag className="label-tag grey-tag-light ma-0">
            {(source === MANUAL_INGESTION_SOURCE && 'Manual Execution') ||
                (source === SCHEDULED_INGESTION_SOURCE && 'Scheduled Execution') ||
                (source === CLI_INGESTION_SOURCE && 'CLI Execution') ||
                'N/A'}
        </Tag>
    );
}

interface ButtonsColumnProps {
    record: any;
    t: (key: string) => string;
    handleViewDetails: (urn: string) => void;
    handleCancelExecution: (urn: string) => void;
    handleRollbackExecution: (runId: string) => void;
    isLoading: boolean;
    canCancel?: boolean;
}

export function ButtonsColumn({
    t,
    record,
    handleViewDetails,
    handleCancelExecution,
    handleRollbackExecution,
    isLoading,
    canCancel = true,
}: ButtonsColumnProps) {
    return (
        <Wrapper>
            <Dropdown
                trigger={['click']}
                placement="bottomRight"
                overlay={
                    <Menu className="f-dropdown-small">
                        {record.urn && navigator.clipboard && (
                            <Tooltip title={t('Copy Execution Request URN')}>
                                <Menu.Item
                                    onClick={() => {
                                        navigator.clipboard.writeText(record.urn);
                                    }}
                                >
                                    {t('Copy URN')}
                                </Menu.Item>
                            </Tooltip>
                        )}
                        {!!record.duration && (
                            <Menu.Item onClick={() => handleViewDetails(record.urn)}>{t('Details')}</Menu.Item>
                        )}
                        {record.status === RUNNING && (
                            <Tooltip
                                title={!canCancel ? t('UNAUTHORISED_TOOLTIP') : ''}
                                placement="topLeft"
                                overlayClassName="f-tooltip-white"
                            >
                                <span>
                                    <Menu.Item disabled={!canCancel} onClick={() => handleCancelExecution(record.urn)}>
                                        {t('Cancel')}
                                    </Menu.Item>
                                </span>
                            </Tooltip>
                        )}
                        {record.status === SUCCESS && record.showRollback && (
                            <Menu.Item disabled={isLoading} onClick={() => handleRollbackExecution(record.id)}>
                                {t('Rollback')}
                            </Menu.Item>
                        )}
                    </Menu>
                }
            >
                <Button
                    type="text"
                    title={t('More Actions')}
                    icon={<IconMoreVert />}
                    aria-label={t('More Actions')}
                    className="icon-button ma-0"
                />
            </Dropdown>
        </Wrapper>
    );
}
